<template>
    <div>
        <!-- Organizer, if applicable (on locations) -->
        <div v-if="record.type === 'location' && specificRecord" class="pt-3 pl-5 pr-5 pb-5">
            <h4 style="height: 40px">
                {{ $t('event_organizer') }}
                <IconBtn v-if="userIsStaff()" icon-cls="pencil" icon-color="grey" @click="onAssignEoBtn" />
            </h4>
            <LinkedAddressList
                :query-params="{ address_from_event_organizer_id: specificRecord.event_organizer_id || -1 }"
                hide-delete-link
                hide-edit-contact
                @edit-item="onEditAddress"
            />
        </div>

        <!-- Locations, if applicable (on event organizers) -->
        <div v-if="record.type === 'event_organizer' && specificRecord" class="pt-3 pl-5 pr-5 pb-5">
            <h4 style="height: 40px">
                {{ $t('locations') }}
            </h4>
            <LinkedAddressList
                ref="assignedLocationsList"
                :query-params="{ location_addresses_from_event_organizer_id: specificRecord.id || -1 }"
                hide-edit-contact
                @edit-item="onEditAddress"
                @delete-link="onDeleteLocationLink"
            />
        </div>

        <!-- linked contacts -->
        <div class="pt-3 pl-5 pr-5 pb-5">
            <h4 style="height: 40px; padding-top: 8px">{{ $t('assigned_addresses') }}</h4>
            <LinkedAddressList
                ref="assignedAddressesList"
                class="mt-2"
                :load-address-contact-records-for="record.id || -1"
                @edit-item="onEditAddress"
                @edit-contact="onEditContact"
                @delete-link="onDeleteContactLink"
            />
        </div>

        <!-- Organizer of Address Owner/Creator -->
        <div v-if="record" class="pt-3 pl-5 pr-5 pb-5">
            <h4 style="height: 40px">
                {{ $t('creator') }}
            </h4>
            <LinkedUserList :address="record" />
        </div>

        <v-dialog v-model="assignEoDlg" persistent max-width="500px">
            <v-card>
                <v-card-title>
                    <span class="text-h5">{{ $t('veranstalter_zuweisen') }}</span>
                </v-card-title>

                <v-card-text>
                    <p>{{ $t('location_assign_eo_dlg_text') }}</p>

                    <v-autocomplete
                        v-model="selectedEo"
                        :loading="eoLoading"
                        :items="foundEos.models"
                        item-text="title"
                        item-value="id"
                        return-object
                        :search-input.sync="eoSearch"
                        no-filter
                        dense
                        outlined
                        hide-no-data
                        hide-details
                        :label="`${$t('search')} ...`"
                    >
                        <template #item="{ item, on, attrs }">
                            <AddressComboListItem :item="item" v-bind="attrs" v-on="on" />
                        </template>
                    </v-autocomplete>
                </v-card-text>

                <v-card-actions>
                    <slot name="actions">
                        <v-spacer></v-spacer>
                        <v-btn text @click="assignEoDlg = false">{{ $t('cancel') }}</v-btn>
                        <v-btn color="action" text :disabled="!selectedEo" @click="onAssignEo">
                            {{ $t('assign') }}
                        </v-btn>
                    </slot>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <ShowAddressContactDlg
            v-model="showAddressContactDlg"
            :record="showAddressContact"
            @edit-address="
                (event) => {
                    showAddressContactDlg = false;
                    onEditAddress(event);
                }
            "
            @delete-link="
                (event) => {
                    showAddressContactDlg = false;
                    onDeleteContactLink(event);
                }
            "
            @saved="
                (event) => {
                    showAddressContactDlg = false;
                    onEditContactLink(event);
                }
            "
        />
    </div>
</template>

<script>
import { instance as api } from '@/helpers/api';
import { debounce, errToStr } from '@/helpers/tools';
import { Address } from '@/models/Address';
import { Location } from '@/models/Location';
import { EventOrganizer, EventOrganizerCollection } from '@/models/EventOrganizer';
import { userIsStaff, userIsOrganizer } from '@/helpers/auth';
import LinkedAddressList from '@/components/address/LinkedAddressList.vue';
import ShowAddressContactDlg from '@/components/address/ShowAddressContactDlg.vue';
import AddressComboListItem from '@/components/address/AddressComboListItem.vue';
import LinkedUserList from '@/components/user/LinkedUserList.vue';

/**
 * Displays assigned addresses of a specific address.
 */
export default {
    name: 'AssignedAddressesPanel',
    components: {
        LinkedAddressList,
        ShowAddressContactDlg,
        AddressComboListItem,
        LinkedUserList,
    },
    props: {
        record: {
            type: Address,
            required: true,
        },
        specificRecord: {
            type: [Location, EventOrganizer],
            default: null,
        },
    },

    data() {
        return {
            assignEoDlg: false,
            showAddressContactDlg: false,
            showAddressContact: null,

            selectedEo: null,
            eoLoading: false,
            foundEos: new EventOrganizerCollection(),
            eoSearch: '',
        };
    },

    watch: {
        eoSearch(newVal, oldVal) {
            newVal && newVal !== oldVal && newVal.length >= 3 && this.searchEos(newVal);
        },
    },

    methods: {
        userIsStaff,
        userIsOrganizer,
        onEditAddress(address) {
            this.$router.push({ name: 'address-edit', params: { id: address.id } });
        },

        onEditContact(address) {
            this.showAddressContact = address;
            this.showAddressContactDlg = true;
        },

        searchEos: debounce(async function (query) {
            this.eoLoading = true;
            await this.foundEos.query({ query });
            this.eoLoading = false;
        }, 250),

        onAssignEoBtn() {
            this.selectedEo = null;
            this.assignEoDlg = true;
        },

        onAssignEo() {
            if (this.selectedEo) {
                this.$emit('assign-event-organizer', this.selectedEo);
            }
            this.assignEoDlg = false;
        },

        reloadAssignedAddresses() {
            this.$refs.assignedAddressesList.loadAddresses();
        },

        reloadAssignedLocations() {
            this.$refs.assignedLocationsList.loadAddresses();
        },

        /**
         * Shows the dialog to edit an existing address contact record.
         */
        onEditContactLink(linkedAddress) {
            this.showAddressContactDlg = false;
            this.reloadAssignedAddresses();
        },

        async onDeleteContactLink(linkedAddress) {
            // the linked address should have an addressContact property with the AddressContact record to delete.
            // @see LinkedAddressList
            if (linkedAddress.addressContact) {
                try {
                    this.$emit('saving', true);
                    await linkedAddress.addressContact.delete();
                } catch (e) {
                    this.$toast({ message: errToStr(e), color: 'error' });
                } finally {
                    this.$emit('saving', false);
                    this.reloadAssignedAddresses();
                }
            }
        },

        async onDeleteLocationLink(linkedAddress) {
            // the linked address should have an addressContact property with the AddressContact record to delete.
            // @see LinkedAddressList
            if (linkedAddress.location_id) {
                try {
                    this.$emit('saving', true);
                    await api().post('/location/unlinkEventOrganizer', {
                        location_id: linkedAddress.location_id,
                    });
                } catch (e) {
                    this.$toast({ message: errToStr(e), color: 'error' });
                } finally {
                    this.$emit('saving', false);
                    this.reloadAssignedLocations();
                }
            }
        },
    },
};
</script>
